import {OpenRouter} from "@openrouter/sdk";
import {store} from "../../../Store/index.js";
import {Assert} from "js-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {StoryMessageProto} from "../../../Server/DB/storyMessages/@StoryMessage.js";
import {GenerateNextMessageOptions, InsertSummaryDummyMessages, ApplyOmitOldNumberedLists, GenerateNextMessageOptions_Init} from "./@Shared.js";
import {EffortLevel, EffortLevel_ForOpenRouter, ThinkingType} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_StoryExplore.js";

let openRouter: OpenRouter;

export async function GenerateNextMessage_OpenRouter(options: GenerateNextMessageOptions_Init) {
	const opts = new GenerateNextMessageOptions(options);
	if (opts.apiKey == null) throw new Error("OpenRouter API key is not set.");
	if (opts.prevMessages.length == 0) throw new Error("Cannot generate next message without any previous messages.");
	Assert(opts.baseURLOverride == null, "baseURLOverride is not currently supported for OpenRouter requests."); // (forget what it does XD)

	if (openRouter == null) {
		openRouter = new OpenRouter({
			apiKey: opts.apiKey,
			//...(opts.baseURLOverride ? {serverURL: opts.baseURLOverride} : {}),
		});
	} else {
		Assert(openRouter["_options"].apiKey == opts.apiKey, "OpenRouter API key changed; please refresh the page to use the OpenRouter API further.");
	}

	let prevMessages_final = InsertSummaryDummyMessages(opts.prevMessages);

	if (opts.omitOldNumberedLists) {
		prevMessages_final = ApplyOmitOldNumberedLists(prevMessages_final, opts);
	}

	const messages_forAPI = [
		...(opts.systemPrompt != null ? [{role: "system" as const, content: opts.systemPrompt}] : []),
		...prevMessages_final.map(a=>({role: a.role as "user"|"assistant", content: a.content})),
	];

	const providersStr = opts.modelProvidersList;
	const providerList = providersStr
		? providersStr.split(",").map(a=>a.trim()).filter(a=>a.length > 0)
		: [];

	const reasoningEnabled = opts.thinkingType != ThinkingType.disabled;
	if (reasoningEnabled) Assert(opts.effort != EffortLevel.none, `Thinking-type is not disabled; so effort should be set to something other than "none".`);

	const stream = await openRouter.chat.send({
		chatRequest: {
			model: opts.model,
			messages: messages_forAPI,
			stream: true,
			maxCompletionTokens: reasoningEnabled ? opts.thinking_budgetTokens + opts.responseTokens : opts.responseTokens,
			temperature: reasoningEnabled ? 1 : opts.temperature, // temperature must be 1 when thinking is enabled (same as Anthropic)
			reasoning: reasoningEnabled ? {effort: EffortLevel_ForOpenRouter(opts.effort)} : {effort: "none"},
			provider: {
				...(providerList.length > 0 ? {
					order: providerList,
					allowFallbacks: false,
					// commented; empirically confirmed redundant, with the two fields above
					//only: providerList,
				} : {}),
				...(opts.blockTrainingAndRetention ? {
					dataCollection: "deny",
					zdr: true,
				} : {}),
			},
		},
	}).catch(err=>{
		console.log("Got error for OpenRouter request:", err);
		if (err?.statusCode === 429) {
			ShowMessageBox({
				title: "OpenRouter API rate limit reached",
				message: `Rate limit exceeded. (possibly at OR-provider level?)`,
			});
		}
		throw err;
	});

	if (stream) {
		for await (const chunk of stream) {
			const delta = chunk.choices[0]?.delta;
			if (delta?.content) {
				allTextSegmentsStreamedFromOpenRouter.push(delta.content);
				opts.onTextSegment?.(delta.content);
			}
			if (delta?.reasoning) {
				opts.onThinkingSegment?.(delta.reasoning);
			}
			if (chunk.usage?.completionTokens != null) {
				opts.onOutputTokens?.(chunk.usage.completionTokens);
			}
		}
	}
}
export const allTextSegmentsStreamedFromOpenRouter = [] as string[];

/** Fetches the OpenRouter model metadata for the given model ID (e.g. `"anthropic/claude-3-5-sonnet"`).
 * Uses the `/api/v1/model/{author}/{slug}` endpoint (singular "model"), which returns a `{ data: Model }` wrapper.
 * The `data` object includes `supported_parameters`, `architecture`, `pricing`, etc. */
export async function GetOpenRouterModelInfo(modelId: string): Promise<Record<string, any>> {
	// The endpoint path is /model/<author>/<slug> — keep the slash as a path separator, don't encode it.
	const url = `https://openrouter.ai/api/v1/model/${modelId}`;
	const resp = await fetch(url);
	if (!resp.ok) throw new Error(`OpenRouter model info request failed: ${resp.status} ${resp.statusText}`);
	return resp.json();
}